import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  CircularProgress,
  Alert,
  AlertTitle,
  Divider,
  Chip,
  Stack
} from '@mui/material';
import { Home, CheckCircle, Cancel, Lock, ScienceOutlined } from '@mui/icons-material';
import Dashboard from '../components/Dashboard';
import Header from '../components/Header';
import { selectSelectedStudent } from '../store/selectors/studentSelectors';
import {
  createOrder,
  fetchCheckoutOffer,
  formatPaise,
  payOrder,
  CheckoutOffer,
  CheckoutSku,
  PaymentOrder
} from '../services/checkoutService';

/**
 * Checkout for a paid SKU.
 *
 * <b>This screen must never look like a real payment.</b> The processor behind it takes
 * no money — it exists so the paywall can be exercised before the real integration lands.
 * A parent who believed they had paid, and then found they had not, is a worse outcome
 * than having no checkout at all, so the demo banner is not dismissible and the demo
 * instruments are labelled as such throughout.
 *
 * Two steps, mirroring the server and any real processor: price the order, then settle it.
 */

const DemoBanner: React.FC = () => (
  <Alert severity="warning" icon={<ScienceOutlined />} sx={{ mb: 3 }}>
    <AlertTitle>Demo checkout — no payment is taken</AlertTitle>
    Nothing here charges a card and no money moves. This is a stand-in so the paid
    features can be tried out before real payments are switched on.
  </Alert>
);

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const selectedStudent = useSelector(selectSelectedStudent);

  const requestedSku = params.get('sku') ?? 'solutions';

  const [offer, setOffer] = useState<CheckoutOffer | null>(null);
  const [loading, setLoading] = useState(true);
  const [instrumentId, setInstrumentId] = useState<string>('');
  const [coupon, setCoupon] = useState('');
  const [order, setOrder] = useState<PaymentOrder | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedStudent) {
      navigate('/select-student');
    }
  }, [selectedStudent, navigate]);

  useEffect(() => {
    let cancelled = false;
    fetchCheckoutOffer().then((o) => {
      if (cancelled) return;
      setOffer(o);
      // Preselect the first instrument that will actually succeed — a demo should not
      // open on the card designed to fail.
      const firstApproving = o?.instruments.find((i) => !i.willDecline);
      if (firstApproving) setInstrumentId(firstApproving.id);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const sku: CheckoutSku | undefined = offer?.skus.find((s) => s.sku === requestedSku);

  const handlePay = useCallback(async () => {
    if (!sku || !instrumentId) return;
    setBusy(true);
    setError(null);

    // Re-price on every attempt rather than reusing a failed order: a decline leaves the
    // order FAILED, and the server will not settle it a second time.
    const created = await createOrder(sku.sku, coupon.trim() || undefined);
    if (created.state === 'unavailable') {
      setUnavailable(created.message);
      setBusy(false);
      return;
    }
    if (created.state === 'error') {
      setError(created.message);
      setBusy(false);
      return;
    }

    const paid = await payOrder(created.order.orderId, instrumentId);
    setBusy(false);

    if (paid.state === 'unavailable') {
      setUnavailable(paid.message);
      return;
    }
    if (paid.state === 'error') {
      setError(paid.message);
      return;
    }
    setOrder(paid.order);
  }, [sku, instrumentId, coupon]);

  if (!selectedStudent) return null;

  const body = (() => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (unavailable || !offer?.available) {
      return (
        <Paper sx={{ p: 4, my: 4, textAlign: 'center' }}>
          <Lock sx={{ fontSize: 44, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h5" sx={{ mb: 1 }}>
            Purchases are not available yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 460, mx: 'auto' }}>
            {unavailable ??
              'This site has no payment processor connected yet, so nothing can be bought here. Ask your school about access.'}
          </Typography>
          <Button variant="contained" startIcon={<Home />} onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </Paper>
      );
    }

    // ── Settled ────────────────────────────────────────────────────────────
    if (order?.status === 'PAID') {
      return (
        <Box sx={{ py: 3 }}>
          <DemoBanner />
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircle color="success" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h5" sx={{ mb: 1 }}>
              Unlocked
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {sku?.name} is now active on this account.
              {order.reference && (
                <>
                  {' '}
                  Reference <b>{order.reference}</b>.
                </>
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
              No payment was taken — this was a demo transaction.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
              <Button variant="contained" onClick={() => navigate('/practice')}>
                Start practising
              </Button>
              <Button variant="outlined" onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </Button>
            </Stack>
          </Paper>
        </Box>
      );
    }

    if (!sku) {
      return (
        <Paper sx={{ p: 4, my: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Nothing is on sale under that id
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            "{requestedSku}" is not something that can be bought here.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </Paper>
      );
    }

    // ── Buy ────────────────────────────────────────────────────────────────
    return (
      <Box sx={{ py: 3 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          Checkout
        </Typography>

        <DemoBanner />

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6">{sku.name}</Typography>
          {sku.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {sku.description}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 2 }}>
            <Typography variant="h4">{formatPaise(sku.amountPaise)}</Typography>
            <Typography variant="body2" color="text.secondary">
              {sku.validDays ? `for ${sku.validDays} days` : 'one-time purchase'}
            </Typography>
          </Box>
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Payment method
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These are demo instruments. None of them is a real account, and none of them is
            charged.
          </Typography>

          <RadioGroup value={instrumentId} onChange={(e) => setInstrumentId(e.target.value)}>
            {offer.instruments.map((i) => (
              <FormControlLabel
                key={i.id}
                value={i.id}
                control={<Radio />}
                disabled={busy}
                sx={{
                  m: 0,
                  mb: 1,
                  px: 1.5,
                  py: 0.5,
                  border: '1px solid',
                  borderColor: instrumentId === i.id ? 'primary.main' : 'divider',
                  borderRadius: 1
                }}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">{i.label}</Typography>
                    {i.willDecline && (
                      <Chip size="small" color="error" variant="outlined" label="declines" />
                    )}
                  </Box>
                }
              />
            ))}
          </RadioGroup>

          <TextField
            size="small"
            label="Coupon code (optional)"
            value={coupon}
            onChange={(e) => setCoupon(e.target.value)}
            disabled={busy}
            sx={{ mt: 2 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            The discount is worked out on the server. An unusable code is ignored rather
            than blocking the purchase.
          </Typography>
        </Paper>

        {order?.status === 'FAILED' && (
          <Alert severity="error" icon={<Cancel />} sx={{ mb: 3 }}>
            <AlertTitle>Payment declined</AlertTitle>
            {order.failureReason ?? 'That payment method was declined.'} Nothing was
            charged — pick another method and try again.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Divider sx={{ mb: 3 }} />

        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Button
            variant="contained"
            size="large"
            disabled={busy || !instrumentId}
            onClick={handlePay}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {busy ? 'Processing…' : `Pay ${formatPaise(sku.amountPaise)} (demo)`}
          </Button>
          <Button variant="text" onClick={() => navigate(-1)} disabled={busy}>
            Cancel
          </Button>
        </Stack>
      </Box>
    );
  })();

  return (
    <Dashboard
      constrained
      header={<Header selectedStudent={selectedStudent} />}
      students={body}
    />
  );
};

export default CheckoutPage;
